import { useRef, useState, useCallback } from "react";
import {
  ChevronDown,
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  Loader2,
  ExternalLink,
  Video,
  SkipBack,
  SkipForward,
  X,
} from "lucide-react";
import { useAudioPlayer, SPEED_OPTIONS } from "@/contexts/AudioPlayerContext";
import {
  formatChapterTimestamp,
  getCurrentChapter,
  getNextChapter,
  getPreviousChapter,
} from "@scroll/shared-utils";

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function formatChapterDuration(
  startTimeMs: number,
  nextStartTimeMs: number | undefined,
  durationMs: number,
): string | null {
  const endTimeMs = nextStartTimeMs ?? durationMs;
  if (!Number.isFinite(endTimeMs) || endTimeMs <= startTimeMs) return null;

  const totalMinutes = Math.max(1, Math.round((endTimeMs - startTimeMs) / 60_000));
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${totalMinutes} min`;
}

interface FullPlayerProps {
  onClose: () => void;
}

export default function FullPlayer({ onClose }: FullPlayerProps) {
  const {
    currentEpisode,
    isPlaying,
    isBuffering,
    positionMs,
    durationMs,
    playbackSpeed,
    pause,
    resume,
    seekTo,
    skipForward,
    skipBack,
    setSpeed,
    stop,
  } = useAudioPlayer();

  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(0);
  const scrubberRef = useRef<HTMLDivElement>(null);

  const getPositionFromEvent = useCallback(
    (clientX: number) => {
      const bar = scrubberRef.current;
      if (!bar || !durationMs) return 0;
      const rect = bar.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return pct * durationMs;
    },
    [durationMs],
  );

  const handleScrubStart = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      const pos = getPositionFromEvent(e.clientX);
      setDragPosition(pos);

      const onMove = (ev: MouseEvent) => {
        setDragPosition(getPositionFromEvent(ev.clientX));
      };
      const onUp = (ev: MouseEvent) => {
        const finalPos = getPositionFromEvent(ev.clientX);
        seekTo(finalPos);
        setIsDragging(false);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [getPositionFromEvent, seekTo],
  );

  const handleTouchScrubStart = useCallback(
    (e: React.TouchEvent) => {
      setIsDragging(true);
      const pos = getPositionFromEvent(e.touches[0].clientX);
      setDragPosition(pos);

      const onMove = (ev: TouchEvent) => {
        ev.preventDefault();
        setDragPosition(getPositionFromEvent(ev.touches[0].clientX));
      };
      const onEnd = (ev: TouchEvent) => {
        const touch = ev.changedTouches[0];
        seekTo(getPositionFromEvent(touch.clientX));
        setIsDragging(false);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
      };
      window.addEventListener("touchmove", onMove, { passive: false });
      window.addEventListener("touchend", onEnd);
    },
    [getPositionFromEvent, seekTo],
  );

  if (!currentEpisode) return null;

  const displayPos = isDragging ? dragPosition : positionMs;
  const progress = durationMs > 0 ? (displayPos / durationMs) * 100 : 0;
  const chapters = currentEpisode.chapterMarkers ?? [];
  const currentChapter = getCurrentChapter(chapters, positionMs);
  const previousChapter = getPreviousChapter(chapters, positionMs);
  const nextChapter = getNextChapter(chapters, positionMs);

  return (
    <div className="full-player-overlay" onClick={onClose}>
      <div className="full-player" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="full-player-header">
          <button onClick={onClose} className="full-player-close" aria-label="Close">
            <ChevronDown size={28} />
          </button>
          <span className="full-player-header-title">Now Playing</span>
          <button
            onClick={() => { stop(); onClose(); }}
            className="full-player-close"
            aria-label="Stop"
          >
            <X size={20} />
          </button>
        </div>

        {/* Artwork */}
        <div className="full-player-artwork-container">
          {currentEpisode.artworkUrl ? (
            <img
              src={currentEpisode.artworkUrl}
              alt=""
              className="full-player-artwork"
            />
          ) : (
            <div className="full-player-artwork full-player-artwork-placeholder" />
          )}
        </div>

        {/* Metadata */}
        <div className="full-player-meta">
          {currentEpisode.podcastTitle && (
            <p className="full-player-podcast-title">{currentEpisode.podcastTitle}</p>
          )}
          <h2 className="full-player-episode-title">{currentEpisode.title}</h2>
        </div>

        {/* Scrubber */}
        <div className="full-player-scrubber">
          {chapters.length > 1 ? (
            <div className="full-player-chapter-chip" aria-live="polite">
              <button
                type="button"
                onClick={() => previousChapter && seekTo(previousChapter.startTimeMs)}
                className="full-player-chapter-chip-btn"
                aria-label="Previous chapter"
                disabled={!previousChapter}
              >
                <SkipBack size={16} fill="currentColor" />
              </button>
              <span className="full-player-current-chapter">
                {currentChapter?.title ?? "Chapters"}
              </span>
              <button
                type="button"
                onClick={() => nextChapter && seekTo(nextChapter.startTimeMs)}
                className="full-player-chapter-chip-btn"
                aria-label="Next chapter"
                disabled={!nextChapter}
              >
                <SkipForward size={16} fill="currentColor" />
              </button>
            </div>
          ) : currentChapter ? (
            <p className="full-player-current-chapter" aria-live="polite">
              {currentChapter.title}
            </p>
          ) : null}
          <div
            ref={scrubberRef}
            className="full-player-scrub-track"
            onMouseDown={handleScrubStart}
            onTouchStart={handleTouchScrubStart}
          >
            <div
              className="full-player-scrub-fill"
              style={{ width: `${progress}%` }}
            />
            {/* Chapter tick marks */}
            {durationMs > 0 &&
              chapters.map((c) => {
                const ratio = c.startTimeMs / durationMs;
                if (ratio <= 0 || ratio >= 1) return null;
                return (
                  <span
                    key={`tick-${c.startTimeMs}`}
                    aria-hidden="true"
                    className="full-player-scrub-tick"
                    style={{ left: `${ratio * 100}%` }}
                  />
                );
              })}
            <div
              className="full-player-scrub-thumb"
              style={{ left: `${progress}%` }}
            />
          </div>
          <div className="full-player-times">
            <span>{formatTime(displayPos)}</span>
            <span>{durationMs > 0 ? formatTime(durationMs) : "--:--"}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="full-player-controls">
          <button onClick={() => skipBack(15)} className="full-player-skip-btn" aria-label="Skip back 15 seconds">
            <RotateCcw size={24} />
            <span className="full-player-skip-label">15</span>
          </button>

          <button
            onClick={() => (isPlaying ? pause() : resume())}
            className="full-player-play-btn"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isBuffering ? (
              <Loader2 size={28} className="animate-spin" />
            ) : isPlaying ? (
              <Pause size={28} fill="currentColor" />
            ) : (
              <Play size={28} fill="currentColor" />
            )}
          </button>

          <button onClick={() => skipForward(30)} className="full-player-skip-btn" aria-label="Skip forward 30 seconds">
            <RotateCw size={24} />
            <span className="full-player-skip-label">30</span>
          </button>
        </div>

        {currentEpisode.videoSource?.sourceUrl && (
          <a
            href={currentEpisode.videoSource.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="full-player-watch-link"
          >
            <Video size={16} />
            <span>Watch video</span>
            <ExternalLink size={14} />
          </a>
        )}

        {/* Speed selector */}
        <div className="full-player-speed-container">
          <button
            className="full-player-speed-btn"
            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
          >
            {playbackSpeed}x
          </button>
          {showSpeedMenu && (
            <div className="full-player-speed-menu">
              {SPEED_OPTIONS.map((s) => (
                <button
                  key={s}
                  className={`full-player-speed-option ${s === playbackSpeed ? "active" : ""}`}
                  onClick={() => {
                    setSpeed(s);
                    setShowSpeedMenu(false);
                  }}
                >
                  {s}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Chapters */}
        {chapters.length > 0 && (
          <div className="full-player-chapters">
            <p className="full-player-section-label">Chapters</p>
            <ul className="full-player-chapter-list">
              {chapters.map((c, index) => {
                const isActive = currentChapter?.startTimeMs === c.startTimeMs;
                const chapterDuration = formatChapterDuration(
                  c.startTimeMs,
                  chapters[index + 1]?.startTimeMs,
                  durationMs,
                );
                return (
                  <li key={`chapter-${c.startTimeMs}`}>
                    <button
                      type="button"
                      onClick={() => seekTo(c.startTimeMs)}
                      aria-current={isActive ? "true" : undefined}
                      className={`full-player-chapter-row${isActive ? " is-active" : ""}`}
                    >
                      <span className="full-player-chapter-time">
                        {formatChapterTimestamp(c.startTimeMs)}
                      </span>
                      <span className="full-player-chapter-main">
                        <span className="full-player-chapter-title">{c.title}</span>
                        {chapterDuration && (
                          <span className="full-player-chapter-duration">
                            {chapterDuration}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Description */}
        {currentEpisode.description && (
          <div className="full-player-description">
            <p>{currentEpisode.description.replace(/<[^>]*>/g, "")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
